import React from 'react';
import './TopicCard.css';
import { resolveTopicIcon } from '../utils/topicIconMap';

const TYPE_META = {
  curriculum: {
    label: 'Core',
    icon: '⭐',
    classModifier: 'topic-card--curriculum',
  },
  foundation: {
    label: 'Warm-up',
    icon: '🧱',
    classModifier: 'topic-card--foundation',
  },
  advanced: {
    label: 'Boost',
    icon: '🚀',
    classModifier: 'topic-card--advanced',
  },
};

// Priority status helper using attempts, level, and timestamps
const getTopicStatus = (topic) => {
  const attempts = topic?.attemptsCount ?? topic?.attempts ?? 0;
  const level = topic?.currentLevel ?? topic?.level ?? 1;
  const isMastered = topic?.isMastered || topic?.completed || level >= 5;

  if (isMastered) {
    return {
      label: '🏆 Mastered',
      classModifier: 'topic-status--mastered',
      isRecommended: false,
    };
  }

  // Struggling: Multiple attempts but still stuck at Lvl 1 or 2
  if (attempts >= 4 && level <= 2) {
    return {
      label: '⚡ Power-Up',
      classModifier: 'topic-status--powerup',
      isRecommended: true,
    };
  }

  // Spaced repetition: Inactive for 5+ days
  if (topic?.lastAttemptedAt) {
    const daysSince = Math.floor(
      (Date.now() - new Date(topic.lastAttemptedAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (attempts > 0 && daysSince >= 5) {
      return {
        label: '🔄 Boost',
        classModifier: 'topic-status--refresh',
        isRecommended: true,
      };
    }
  }

  // Steady Progress
  if (attempts > 0) {
    return {
      label: '🚀 In Progress',
      classModifier: 'topic-status--progress',
      isRecommended: false,
    };
  }

  // Fresh
  return {
    label: '✨ New',
    classModifier: 'topic-status--new',
    isRecommended: false,
  };
};

const TopicCard = ({
  topic,
  color = '#4F46E5',
  onClick,
  disabled = false,
  topicType,
}) => {
  const resolvedType = (topicType || topic?.topicType || 'curriculum').toLowerCase();
  const meta = TYPE_META[resolvedType] || TYPE_META.curriculum;
  const status = getTopicStatus(topic);

  const handleClick = () => {
    if (disabled) return;
    if (onClick) {
      onClick(topic.id, topic.name);
    }
  };

  return (
    <div className="topic-card-container" style={{ '--subject-color': color }}>
      <div
        className={`topic-card ${meta.classModifier} ${
          !disabled && status.isRecommended ? 'topic-card--recommended' : ''
        } ${disabled ? 'topic-card--disabled' : ''}`}
        onClick={handleClick}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Corner Ribbon / Bookmark */}
        <div className="topic-ribbon">
          <span className="ribbon-icon">{meta.icon}</span>
          <span className="ribbon-text">{meta.label}</span>
        </div>

        {/* Top-Right Level Chip */}
        <div className="topic-level-chip">
          {disabled ? 'Locked' : `Lvl ${topic.currentLevel || 1}`}
        </div>

        {/* Main Body */}
        <div className="topic-body">
          <div className="topic-icon-wrap">
            {resolveTopicIcon(topic, topic.subjectName)}
          </div>

          <div className="topic-info">
            <h4 className="topic-name">{topic.name}</h4>
            <div className="topic-meta-row">
              <span className="topic-attempts">
                {`Attempts: ${topic.attemptsCount ?? 0}`}
              </span>

              {/* Status Indicator Pill */}
              {!disabled && (
                <span className={`topic-status-pill ${status.classModifier}`}>
                  {status.label}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Bottom Tooltip */}
      <div className="topic-card-bottom-tooltip" role="tooltip">
        <div className="tooltip-title">{topic.name}</div>
        {topic.description?.trim() && (
          <>
            <div className="tooltip-divider" />
            <div className="tooltip-description">{topic.description.trim()}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default TopicCard;